import React, { useCallback, useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  CalendarDays,
  ChevronRight,
  CircleDollarSign,
  Filter,
  KeyRound,
  RefreshCcw,
  Search,
  ShieldCheck,
  Users
} from 'lucide-react';

import {
  createAnalyticsCostAssumption,
  getAnalyticsOverview,
  getAnalyticsUserJourney,
  searchAnalyticsUsers
} from '../api/adminService';
import './AdminAnalyticsPage.css';

const SECRET_STORAGE_KEY = 'forzaAdminSecret';
const TABS = ['Overview', 'Lifecycle', 'Engagement', 'Retention', 'Conversion', 'AI Economics', 'Users'];
const QUALITY_LABELS = {
  reliable: 'Reliable',
  partial: 'Partial',
  'forward-only': 'Forward only',
  'low-confidence': 'Low confidence',
  unavailable: 'Unavailable'
};

const toDateInput = (date) => date.toISOString().slice(0, 10);
const getDefaultRange = () => {
  const to = new Date();
  to.setDate(to.getDate() - 1);
  const from = new Date(to);
  from.setDate(from.getDate() - 6);
  return { from: toDateInput(from), to: toDateInput(to) };
};
const formatNumber = (value) => value === null || value === undefined ? '—' : new Intl.NumberFormat('en').format(value);
const formatPercent = (value) => value === null || value === undefined ? '—' : `${value}%`;
const formatDate = (value) => value ? new Date(value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
const formatMoney = (value, currency = 'EUR') => value === null || value === undefined
  ? 'Not configured'
  : new Intl.NumberFormat('en', { style: 'currency', currency, maximumFractionDigits: 4 }).format(value);

const Quality = ({ value = 'reliable' }) => (
  <span className={`analytics-quality analytics-quality--${value}`}>{QUALITY_LABELS[value] || value}</span>
);

const Metric = ({ label, metric, icon: Icon }) => (
  <article className="analytics-metric">
    <div className="analytics-metric__label">{Icon ? <Icon size={15} /> : null}{label}</div>
    <strong>{formatNumber(metric?.value)}</strong>
    {metric?.rate !== undefined && metric?.rate !== null ? <span className="analytics-metric__rate">{formatPercent(metric.rate)}</span> : null}
    <Quality value={metric?.quality} />
  </article>
);

const SectionHeading = ({ title, description, action }) => (
  <div className="analytics-section-heading">
    <div>
      <h2>{title}</h2>
      {description ? <p>{description}</p> : null}
    </div>
    {action}
  </div>
);

const EmptyState = ({ children }) => <div className="analytics-empty">{children}</div>;

const Funnel = ({ stages = [] }) => {
  const denominator = stages[0]?.count || 0;
  return (
    <div className="analytics-funnel">
      {stages.map((stage) => {
        const width = stage.count === null || denominator === 0 ? 18 : Math.max(18, (stage.count / denominator) * 100);
        return (
          <div className="analytics-funnel__row" key={stage.key}>
            <span>{stage.label}</span>
            <div className="analytics-funnel__track">
              <i style={{ width: `${width}%` }} className={stage.count === null ? 'is-forward' : ''} />
            </div>
            <b>{formatNumber(stage.count)}</b>
            <Quality value={stage.quality} />
          </div>
        );
      })}
    </div>
  );
};

const DataTable = ({ columns, rows, rowKey = 'key' }) => (
  <div className="analytics-table-wrap">
    <table className="analytics-table">
      <thead><tr>{columns.map((column) => <th key={column.key}>{column.label}</th>)}</tr></thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row[rowKey]}>
            {columns.map((column) => <td key={column.key}>{column.render ? column.render(row) : row[column.key]}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AdminAnalyticsPage = () => {
  const defaultRange = useMemo(getDefaultRange, []);
  const [adminSecret, setAdminSecret] = useState(() => sessionStorage.getItem(SECRET_STORAGE_KEY) || '');
  const [filters, setFilters] = useState({ ...defaultRange, timezone: 'Europe/Rome', username: '', excludeUsername: '' });
  const [activeTab, setActiveTab] = useState('Overview');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [userQuery, setUserQuery] = useState('');
  const [journey, setJourney] = useState(null);
  const [costForm, setCostForm] = useState({
    featureKey: '',
    displayName: '',
    unit: 'request',
    averageCost: '',
    currency: 'EUR',
    effectiveFrom: defaultRange.to
  });
  const [savingCost, setSavingCost] = useState(false);

  const loadDashboard = useCallback(async () => {
    if (!adminSecret.trim()) {
      setError('Enter the admin secret to load analytics.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      sessionStorage.setItem(SECRET_STORAGE_KEY, adminSecret);
      const response = await getAnalyticsOverview({ adminSecret, filters });
      setData(response.data);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Unable to load analytics.');
    } finally {
      setLoading(false);
    }
  }, [adminSecret, filters]);

  const handleUserSearch = async () => {
    setError('');
    try {
      const response = await searchAnalyticsUsers({ adminSecret, username: userQuery });
      setUsers(response.data?.users || []);
      setJourney(null);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Unable to search users.');
    }
  };

  const handleJourney = async (userId) => {
    setError('');
    try {
      const response = await getAnalyticsUserJourney({ adminSecret, userId });
      setJourney(response.data);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Unable to load this user journey.');
    }
  };

  const handleCostSave = async (event) => {
    event.preventDefault();
    setSavingCost(true);
    setError('');
    try {
      await createAnalyticsCostAssumption({ adminSecret, assumption: costForm });
      setCostForm((current) => ({ ...current, featureKey: '', displayName: '', averageCost: '' }));
      await loadDashboard();
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Unable to save the cost assumption.');
    } finally {
      setSavingCost(false);
    }
  };

  const featureColumns = [
    { key: 'name', label: 'Feature', render: (row) => <strong>{row.name}</strong> },
    { key: 'users', label: 'Users', render: (row) => formatNumber(row.users) },
    { key: 'uses', label: 'Uses', render: (row) => formatNumber(row.uses) },
    { key: 'adoptionRate', label: 'Adoption', render: (row) => formatPercent(row.adoptionRate) },
    { key: 'quality', label: 'Data quality', render: (row) => <Quality value={row.quality} /> }
  ];
  const questColumns = [
    { key: 'type', label: 'Quest type', render: (row) => <strong className="analytics-capitalize">{row.type}</strong> },
    { key: 'scheduled', label: 'Scheduled', render: (row) => formatNumber(row.scheduled) },
    { key: 'completed', label: 'Completed', render: (row) => formatNumber(row.completed) },
    { key: 'completionRate', label: 'Completion rate', render: (row) => formatPercent(row.completionRate) },
    { key: 'participatingUsers', label: 'Participants', render: (row) => formatNumber(row.participatingUsers) },
    { key: 'averagePerActiveUser', label: 'Per active user', render: (row) => formatNumber(row.averagePerActiveUser) }
  ];

  const renderOverview = () => (
    <>
      <div className="analytics-metrics">
        <Metric label="Registered" metric={data.metrics.registered} icon={Users} />
        <Metric label="Activated" metric={data.metrics.activated} icon={ShieldCheck} />
        <Metric label="Login active" metric={data.metrics.loginActive} icon={Activity} />
        <Metric label="Meaningful active" metric={data.metrics.meaningfulActive} icon={ArrowUpRight} />
        <Metric label="7d inactivity risk" metric={data.metrics.inactivityRisk} icon={AlertTriangle} />
      </div>
      <div className="analytics-overview-grid">
        <section className="analytics-panel analytics-panel--observations">
          <SectionHeading title="Weekly observations" description="Factual rules only; no causal claims." />
          {data.observations.length ? data.observations.map((item) => (
            <div className={`analytics-observation is-${item.tone}`} key={item.rule}>
              {item.tone === 'positive' ? <ArrowUpRight size={18} /> : item.tone === 'negative' ? <ArrowDownRight size={18} /> : <AlertTriangle size={18} />}
              <p>{item.text}</p>
            </div>
          )) : <EmptyState>No threshold rule fired for this period.</EmptyState>}
        </section>
        <section className="analytics-panel">
          <SectionHeading title="Activation funnel" description="Current state for users registered in the selected period." />
          <Funnel stages={data.lifecycle.stages.slice(0, 5)} />
        </section>
        <section className="analytics-panel">
          <SectionHeading title="Retention" description="Login retention by registration cohort." />
          <div className="analytics-retention">
            {data.retention.map((row) => (
              <div key={row.key}>
                <span>{row.key.toUpperCase()}</span>
                <strong>{formatPercent(row.rate)}</strong>
                <small>{row.retained}/{row.eligible}</small>
                <Quality value={row.quality} />
              </div>
            ))}
          </div>
        </section>
      </div>
      <section className="analytics-panel">
        <SectionHeading title="Feature adoption" description="Unique users and recorded usage in the selected period." />
        <DataTable columns={featureColumns} rows={data.features} />
      </section>
    </>
  );

  const renderContent = () => {
    if (!data) return null;
    if (activeTab === 'Overview') return renderOverview();
    if (activeTab === 'Lifecycle') {
      return <section className="analytics-panel"><SectionHeading title="Lifecycle funnel" description="Forward-only stages are never inferred from current state." /><Funnel stages={data.lifecycle.stages} /></section>;
    }
    if (activeTab === 'Engagement') {
      return <>
        <section className="analytics-panel"><SectionHeading title="Quest engagement" description="Scheduled versus completed quests." /><DataTable columns={questColumns} rows={data.questEngagement} rowKey="type" /></section>
        <section className="analytics-panel"><SectionHeading title="Feature engagement" description="Adoption and request/session volume." /><DataTable columns={featureColumns} rows={data.features} /></section>
      </>;
    }
    if (activeTab === 'Retention') {
      return <section className="analytics-panel"><SectionHeading title="Retention cohorts" description="Cohorts below five eligible users are marked low confidence." /><div className="analytics-retention analytics-retention--large">{data.retention.map((row) => <div key={row.key}><span>{row.key.toUpperCase()} retention</span><strong>{formatPercent(row.rate)}</strong><small>{row.retained} retained / {row.eligible} eligible</small><Quality value={row.quality} /></div>)}</div></section>;
    }
    if (activeTab === 'Conversion') {
      return <section className="analytics-panel"><SectionHeading title="Conversion and churn" description="Four funnels remain separate to preserve actionable boundaries." /><DataTable rows={data.conversions} columns={[
        { key: 'label', label: 'Funnel', render: (row) => <strong>{row.label}</strong> },
        { key: 'entered', label: 'Entered', render: (row) => formatNumber(row.entered) },
        { key: 'converted', label: 'Converted', render: (row) => formatNumber(row.converted) },
        { key: 'rate', label: 'Conversion', render: (row) => formatPercent(row.rate) },
        { key: 'quality', label: 'Data quality', render: (row) => <Quality value={row.quality} /> }
      ]} /><div className="analytics-churn"><div><span>7d product inactivity risk</span><strong>{formatNumber(data.churn.productInactivityRisk)}</strong></div><div><span>Subscription downgrades</span><strong>{formatNumber(data.churn.subscriptionDowngrades)}</strong></div><Quality value={data.churn.quality} /></div></section>;
    }
    if (activeTab === 'AI Economics') {
      return <div className="analytics-economics-grid">
        <section className="analytics-panel">
          <SectionHeading title="Estimated AI economics" description="Calculated from internal volume and effective-dated assumptions." />
          <DataTable rows={data.economics.rows} rowKey="featureKey" columns={[
            { key: 'featureKey', label: 'Feature', render: (row) => <strong>{row.featureKey}</strong> },
            { key: 'volume', label: 'Volume', render: (row) => formatNumber(row.volume) },
            { key: 'unit', label: 'Unit' },
            { key: 'averageCost', label: 'Average cost', render: (row) => formatMoney(row.averageCost, row.currency) },
            { key: 'estimatedCost', label: 'Estimated cost', render: (row) => formatMoney(row.estimatedCost, row.currency) },
            { key: 'quality', label: 'Quality', render: (row) => <Quality value={row.quality} /> }
          ]} />
          <div className="analytics-cost-summary">
            <div><span>Total estimated</span><strong>{formatMoney(data.economics.totalEstimatedCost)}</strong></div>
            <div><span>Per registered user</span><strong>{formatMoney(data.economics.perRegisteredUser)}</strong></div>
            <div><span>Per login-active user</span><strong>{formatMoney(data.economics.perLoginActiveUser)}</strong></div>
            <div><span>Per meaningful user</span><strong>{formatMoney(data.economics.perMeaningfulActiveUser)}</strong></div>
          </div>
        </section>
        <form className="analytics-panel analytics-cost-form" onSubmit={handleCostSave}>
          <SectionHeading title="Add cost assumption" description="A new effective date preserves historical calculations." />
          <label>Feature key<input required value={costForm.featureKey} onChange={(event) => setCostForm({ ...costForm, featureKey: event.target.value })} placeholder="voice_coach" /></label>
          <label>Display name<input required value={costForm.displayName} onChange={(event) => setCostForm({ ...costForm, displayName: event.target.value })} placeholder="Voice Coach" /></label>
          <div className="analytics-form-row">
            <label>Billing unit<select value={costForm.unit} onChange={(event) => setCostForm({ ...costForm, unit: event.target.value })}><option>request</option><option>minute</option><option>second</option><option>character</option><option>image</option><option>token</option></select></label>
            <label>Average cost<input required min="0" step="0.000001" type="number" value={costForm.averageCost} onChange={(event) => setCostForm({ ...costForm, averageCost: event.target.value })} /></label>
          </div>
          <div className="analytics-form-row">
            <label>Currency<input required maxLength="3" value={costForm.currency} onChange={(event) => setCostForm({ ...costForm, currency: event.target.value.toUpperCase() })} /></label>
            <label>Effective from<input required type="date" value={costForm.effectiveFrom} onChange={(event) => setCostForm({ ...costForm, effectiveFrom: event.target.value })} /></label>
          </div>
          <button className="analytics-primary-button" disabled={savingCost}>{savingCost ? 'Saving…' : 'Save assumption'}</button>
        </form>
      </div>;
    }
    return <div className="analytics-users-grid">
      <section className="analytics-panel">
        <SectionHeading title="User search" description="Search by username; sensitive health and AI content is excluded." />
        <div className="analytics-search"><input value={userQuery} onChange={(event) => setUserQuery(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') void handleUserSearch(); }} placeholder="Username" /><button onClick={handleUserSearch}><Search size={16} />Search</button></div>
        <div className="analytics-user-list">{users.map((user) => <button key={user._id} onClick={() => handleJourney(user._id)}><span><strong>{user.username}</strong><small>{user.preferredLocale} · {user.subscription?.status || 'free-tier'}</small></span><ChevronRight size={17} /></button>)}</div>
      </section>
      <section className="analytics-panel">
        <SectionHeading title={journey?.user?.username || 'User journey'} description={journey ? `${journey.user.locale} · ${journey.user.subscriptionTier}` : 'Select a user to inspect their privacy-safe timeline.'} />
        {journey ? <div className="analytics-timeline">{journey.timeline.map((item, index) => <div key={`${item.at}-${item.type}-${index}`}><i /><time>{formatDate(item.at)}</time><strong>{item.label}</strong>{item.quality ? <Quality value={item.quality} /> : null}</div>)}</div> : <EmptyState>No user selected.</EmptyState>}
      </section>
    </div>;
  };

  return (
    <main className="admin-analytics">
      <header className="analytics-header">
        <div><h1>Forza Analytics</h1><p>Founder command center</p></div>
        <div className="analytics-secret"><KeyRound size={16} /><input type="password" value={adminSecret} onChange={(event) => setAdminSecret(event.target.value)} placeholder="Admin secret" /></div>
      </header>

      <nav className="analytics-tabs" aria-label="Analytics sections">
        {TABS.map((tab) => <button className={activeTab === tab ? 'is-active' : ''} key={tab} onClick={() => setActiveTab(tab)}>{tab}</button>)}
      </nav>

      <section className="analytics-toolbar">
        <label><CalendarDays size={15} />From<input type="date" value={filters.from} onChange={(event) => setFilters({ ...filters, from: event.target.value })} /></label>
        <label>To<input type="date" value={filters.to} onChange={(event) => setFilters({ ...filters, to: event.target.value })} /></label>
        <label><Filter size={15} />Include username<input value={filters.username} onChange={(event) => setFilters({ ...filters, username: event.target.value, excludeUsername: '' })} placeholder="All users" /></label>
        <label>Exclude username<input value={filters.excludeUsername} onChange={(event) => setFilters({ ...filters, excludeUsername: event.target.value, username: '' })} placeholder="None" /></label>
        <button className="analytics-primary-button" onClick={loadDashboard} disabled={loading}>{loading ? <RefreshCcw className="is-spinning" size={16} /> : <RefreshCcw size={16} />}{loading ? 'Loading…' : 'Apply'}</button>
      </section>

      {error ? <div className="analytics-error">{error}</div> : null}
      {!data && !loading ? <section className="analytics-welcome"><CircleDollarSign size={30} /><h2>Connect to product analytics</h2><p>Enter the admin secret, choose a reporting period, and load the dashboard.</p><button className="analytics-primary-button" onClick={loadDashboard}>Load analytics</button></section> : null}
      {data ? <div className="analytics-content">{renderContent()}<footer>All dates use {data.period.timezone}. Metrics show their historical coverage explicitly.</footer></div> : null}
    </main>
  );
};

export default AdminAnalyticsPage;
